import { FC, useEffect, useState } from 'react';
import { Encounter, Media } from '@medplum/fhirtypes';
import { Modal, Image, Box, Button, Text, Center } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';
import { formatDateTime } from '@medplum/core';
import classes from './BigCarouselModal.module.css';
import { useTranslation } from 'react-i18next';
import { IconTrash } from '@tabler/icons-react';

interface BigCarouselModalProps {
  images: Media[];
  open: boolean;
  onClose: () => void;
  initialSlide: number;
  getUrl: (attachment: Media['content']) => string | undefined;
  encounter?: Encounter;
  onDelete?: (index: number) => void;
  age?: string | null;
}

const BigCarouselModal: FC<BigCarouselModalProps> = ({
  images,
  open,
  onClose,
  initialSlide,
  getUrl,
  encounter,
  onDelete,
  age,
}) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const { t } = useTranslation(['common', 'images-tab']);

  // Reset currentSlide if initialSlide changes or modal opens
  useEffect(() => {
    if (open) {
      setCurrentSlide(initialSlide);
    }
  }, [initialSlide, open]);

  const encounterDate = encounter?.period?.start
    ? formatDateTime(encounter.period.start)
    : '';

  return (
    <Modal
      opened={open}
      onClose={onClose}
      size="xl"
      centered
      title={
        encounterDate
          ? `${t('encounter', { ns: 'images-tab' })}: ${encounterDate}`
          : ''
      }
    >
      <Carousel
        initialSlide={initialSlide}
        withIndicators
        withControls={images.length > 1}
        loop
        onSlideChange={setCurrentSlide}
      >
        {images.map((media) => {
          const url = getUrl(media.content);
          return (
            <Carousel.Slide key={media.id}>
              <Box className={classes.carouselImageContainer}>
                <Image
                  src={url}
                  alt={
                    media.content?.title ||
                    t('encounterImageAlt', { ns: 'images-tab' })
                  }
                  className={classes.carouselImage}
                />
              </Box>
              {onDelete && (
                <Button
                  leftSection={<IconTrash size={14} />}
                  variant="transparent"
                  color="gray"
                  className={classes.deleteButton}
                  onClick={() => onDelete(currentSlide)}
                  aria-label={
                    t('deleteImageAria', { ns: 'images-tab' }) as string
                  }
                >
                  {t('delete', { ns: 'common' })}
                </Button>
              )}
            </Carousel.Slide>
          );
        })}
      </Carousel>
      {age && (
        <Center>
          <Text>{age}</Text>
        </Center>
      )}
    </Modal>
  );
};

export default BigCarouselModal;
